import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Store, Action } from '@ngrx/store';
import { Router, ActivatedRoute } from '@angular/router';
import * as supportTicketActions from '../store/actions';
import { Actions } from '@ngrx/effects';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { ModuleConfiguration } from '../security.module.config';
import * as fromStore from '../store/reducers';
import { ActionBase } from '../../core/store/action/ActionBase';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as errorActions from '../../pillar/common/actions/ActionError';
import { FormGroup, Validators } from '@angular/forms';
import { SupportTicketInformationModel } from '../models/SupportTicketInformationModel';
import { CustomOption, QuillModules, QuillEditorComponent } from 'ngx-quill';
import { maxPlainTextLength } from "../../common/validators/maxPlainTextLengthValidator";

@Component({
  selector: 'app-support-ticket-workspace',
  templateUrl: './support-ticket-workspace.component.html',
  styleUrls: ['./support-ticket-workspace.component.scss']
})
export class SupportTicketWorkspaceComponent implements OnInit, OnDestroy {
  supportTicketInformation: SupportTicketInformationModel;
  key: string;
  supportTicketEmailForm: FormGroup;
  messageToUsersForm: FormGroup;
  actionLoading: boolean;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  actionIdentifiers: string[] = [];
  editorModules = {
    toolbar: false,
    keyboard:{
      bindings:{
        tab:{ key: 9, handler(){ return true; } }
      }
    }
  }
  maxMessageToUsersLength = 1000;
  cursorPosition: number = 0;
  messageLength: number;

  viewSupportTicketConfigurationPermission: string = 'ConfigurationModule.SupportTicket:View';
  editSupportTicketConfigurationPermission: string = 'ConfigurationModule.SupportTicket:Edit';

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  @ViewChild('editor')
  editor: QuillEditorComponent;

  constructor(private store: Store<any>,
    private router: Router,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef) { }

  storeDispatch(action: Action) {
    var actionBase = action as ActionBase;
    if (actionBase) {
      this.actionIdentifiers.push(actionBase.identifier);
    }
    this.store.dispatch(action);
  }

  ngOnDestroy() {
    this.storeDispatch(new dashboardActions.ClearSelectionAction(ModuleConfiguration.moduleName, SupportTicketInformationModel));
    this.destroyed$.next(true);
  }

  refreshSupportTicketInformation() {
    this.store.dispatch(new supportTicketActions.GetSupportTicketInformationAction());
  }

  ngOnInit() {
    this.refreshSupportTicketInformation();
    this.store
      .select(fromStore.getSupportTicketInformation)
      .takeUntil(this.destroyed$)
      .subscribe((v: SupportTicketInformationModel) => {
        if (v) {
          this.supportTicketInformation = v;
          this.key = v.key;
          if (!this.supportTicketEmailForm) {
            this.initForms();
          }
          this.updateSupportTicketInformationModel();
          this.changeDetectorRef.markForCheck();
        } else {
          this.refreshSupportTicketInformation();
        }
      });

    var updateTypes = [
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, SupportTicketInformationModel, 'supportTicketEmailAddress'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, SupportTicketInformationModel, 'draftMessageToUsersDescription')
    ];

    this.actions$.ofType(errorActions.ACTION_ERROR)
      .takeUntil(this.destroyed$)
      .filter((a: errorActions.ActionError) => this.actionIdentifiers.includes(a.payload.action.identifier))
      .subscribe((a: errorActions.ActionError) => {
        a.payload.errors.forEach(e => this.alerts.addError(e));
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelSuccessAction<SupportTicketInformationModel, any>) => a.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<SupportTicketInformationModel, any>) => {
        a.payload.control.saveSuccess(a.payload.model);
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelErrorTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelErrorAction<SupportTicketInformationModel, any>) => a.payload.action.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelErrorAction<SupportTicketInformationModel, any>) => {
        a.payload.action.payload.control.saveErrors(a.payload.action.payload.model, a.payload.errors);
      });
    this.actions$.ofType(supportTicketActions.ACTIVATE_DRAFT_MESSAGE_TO_USERS_SUCCESS_ACTION)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.actionLoading = false;
        this.alerts.addSuccess('Activate draft message to users success');
        this.changeDetectorRef.markForCheck();
      });
  }

  initForms(): void {
    this.supportTicketEmailForm = this.formBuilder.group({
      key: [''],
      supportTicketEmailAddress: this.formBuilder.control({ value: null, disabled: true },
        Validators.email,       
        null,
        this.destroyed$,
        (control) => {
          this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, SupportTicketInformationModel, 'supportTicketEmailAddress', { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef),
    });

    this.messageToUsersForm = this.formBuilder.group({
      key: [''],
      activeMessageToUsersDescription: [''],
      draftMessageToUsersDescription: ['', Validators.compose([Validators.required, maxPlainTextLength(this.maxMessageToUsersLength)])]
      },
      null,
      this.destroyed$,
      (control) => {
        var model = control.value;
        this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, SupportTicketInformationModel, 'draftMessageToUsersDescription', { key: this.key, model: model, control: control }));
      },
      this.changeDetectorRef
    );
  }

  updateSupportTicketInformationModel() {
    this.supportTicketEmailForm.patchValue(this.supportTicketInformation);
    this.supportTicketEmailForm.markAsPristine();
    this.messageToUsersForm.patchValue(this.supportTicketInformation);
    this.messageToUsersForm.markAsPristine();
  }

  activateMessageToUsers() {
    if(this.messageToUsersForm.valid)
    {
    this.actionLoading = true;
    this.storeDispatch(new supportTicketActions.ActivateDraftMessageToUsersAction());
    }
    else
    {
      this.actionLoading = false;
      this.alerts.addError('Required draft message to activate success');
    }
  }

  back() {
    this.router.navigateByUrl('/administration');
  }

  onChange($event) {
    if ($event.source === 'user') {
      this.cursorPosition = this.editor.quillEditor.getSelection(false);
      this.editor.quillEditor.setSelection(this.cursorPosition, 0, 'user');
    }
    this.messageLength = $event.html == null ? 0 : $event.html.replace(/<\/?[^>]+(>|$)/g, "").trim().length;
  }
}



// WEBPACK FOOTER //
// ./src/app/security/components/support-ticket-workspace.component.ts